<script setup>
import { computed } from "vue";
import DqAvatar from "@/components/shared/DqAvatar.vue";

const props = defineProps({
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  meta: {
    type: Object,
    default: () => ({ total: 0, per_page: 25, current_page: 1 }),
  },
});

const emit = defineEmits(["page-change"]);

const columns = [
  { sorter: false },
  {
    title: "Emp ID",
    dataIndex: "employee_id",
    key: "employee_id",
    sorter: {
      compare: (a, b) =>
        (a.employee_id || "").toLowerCase() >
        (b.employee_id || "").toLowerCase()
          ? 1
          : -1,
    },
  },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: {
      compare: (a, b) =>
        (a.name || "").toLowerCase() > (b.name || "").toLowerCase() ? 1 : -1,
    },
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    sorter: {
      compare: (a, b) =>
        (a.email || "").toLowerCase() > (b.email || "").toLowerCase() ? 1 : -1,
    },
  },
  {
    title: "Department",
    dataIndex: "department",
    key: "department",
    sorter: {
      compare: (a, b) =>
        (a.department || "").toLowerCase() > (b.department || "").toLowerCase()
          ? 1
          : -1,
    },
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    sorter: {
      compare: (a, b) =>
        (a.phone || "").toLowerCase() > (b.phone || "").toLowerCase() ? 1 : -1,
    },
  },
  {
    title: "Joining Date",
    dataIndex: "joining_date",
    key: "joining_date",
    sorter: {
      compare: (a, b) =>
        (a.joining_date || "") > (b.joining_date || "") ? 1 : -1,
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: {
      compare: (a, b) =>
        (a.status || "").toLowerCase() > (b.status || "").toLowerCase()
          ? 1
          : -1,
    },
  },
];

const dataSource = computed(() =>
  (props.items || []).map((e) => ({
    key: e.id,
    ...e,
  }))
);

const pagination = computed(() => ({
  current: props.meta.current_page || 1,
  pageSize: props.meta.per_page || 25,
  total: props.meta.total || dataSource.value.length,
  showSizeChanger: false,
  onChange: (page) => emit("page-change", page),
}));
</script>

<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :row-selection="{ onChange() {}, onSelect() {}, onSelectAll() {} }"
    :loading="loading"
    :pagination="pagination"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'employee_id'">
        <router-link to="/employee/employee-details" class="link-default">
          {{ record.employee_id }}
        </router-link>
      </template>

      <template v-else-if="column.key === 'name'">
        <div class="d-flex align-items-center">
          <a href="javascript:void(0);">
            <DqAvatar
              :src="record.avatar_url || ''"
              :name="record.name"
              size-class="avatar-md"
              shape-class="rounded-circle"
            />
          </a>
          <div class="ms-2">
            <p class="text-dark mb-0">
              <a href="javascript:void(0);">
                {{ record.name }}
              </a>
            </p>
            <span class="fs-12">{{ record.designation || "-" }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'status'">
        <span
          class="d-inline-flex align-items-center badge-xs"
          :class="[
            'badge',
            record.status === 'Active' ? 'badge-success' : 'badge-danger',
          ]"
        >
          <i class="ti ti-point-filled me-1"></i>{{ record.status }}
        </span>
      </template>
    </template>
  </a-table>
</template>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 980px;
  }

  :deep(.dq-report-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>
